import { z, zDecimalNullable, zDecimalRequired } from '@harvestiq/zod';
import { dbCampaignInsertSchema } from './db-campaigns';
import {
  CoverageTiers,
  LivestockInsuranceTypes,
  RmaLgmCommodityCodes,
  RmaLgmTypeCodes,
  RmaLrpCommodityCodes,
  RmaLrpTypeCodes,
} from '@harvestiq/constants';

const baseCreateCampaignRequestSchema = dbCampaignInsertSchema
  .omit({
    createdAt: true,
    updatedAt: true,
    deletedAt: true,
  })
  .extend({
    sendEmailIds: z.array(z.string().uuid()),
    sendSmsIds: z.array(z.string().uuid()),
  });

export const createLrpCampaignRequestSchema = baseCreateCampaignRequestSchema
  .omit({
    insuranceType: true,
    commodity: true,
    typeCode: true,
    deductibles: true,
    liveCattleTargetCwtPerHead: true,
    feederCattleTargetCwtPerHead: true,
    cornTargetBuPerHead: true,
    marketingLengths: true,
  })
  .extend({
    insuranceType: z.literal(LivestockInsuranceTypes.LRP),
    commodity: z.nativeEnum(RmaLrpCommodityCodes),
    typeCode: z.nativeEnum(RmaLrpTypeCodes),
    targetWeight: zDecimalRequired(),
    numberOfHead: z.number().int().min(1),
    coverageTier: z.nativeEnum(CoverageTiers),
  });

export type CreateLrpCampaignRequest = z.input<
  typeof createLrpCampaignRequestSchema
>;

export const createLgmCampaignRequestSchema = baseCreateCampaignRequestSchema
  .omit({
    insuranceType: true,
    commodity: true,
    typeCode: true,
    targetWeight: true,
    coverageTier: true,
    liveCattleTargetCwtPerHead: true,
    feederCattleTargetCwtPerHead: true,
    cornTargetBuPerHead: true,
    marketingLengths: true,
  })
  .extend({
    insuranceType: z.literal(LivestockInsuranceTypes.LGM),
    commodity: z.nativeEnum(RmaLgmCommodityCodes),
    typeCode: z.nativeEnum(RmaLgmTypeCodes),
    deductibles: z.array(z.number().int().min(0).max(150)).nullable(),
    liveCattleTargetCwtPerHead: zDecimalNullable().optional(),
    feederCattleTargetCwtPerHead: zDecimalNullable().nullable().optional(),
    cornTargetBuPerHead: zDecimalNullable().optional(),
    marketingLengths: z
      .array(z.number().int().min(2).max(11))
      .nullable()
      .optional(),
  });

export type CreateLgmCampaignRequest = z.input<
  typeof createLgmCampaignRequestSchema
>;

export const createCampaignRequestSchema = z.discriminatedUnion(
  'insuranceType',
  [createLrpCampaignRequestSchema, createLgmCampaignRequestSchema]
);

export type CreateCampaignRequest = z.input<typeof createCampaignRequestSchema>;
